import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { Syringe, Check, AlertTriangle, Clock, Calendar, ShieldCheck, CheckCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  aplicada: { label: "Aplicada", c: "bg-brand-sage/15 text-brand-sageDark border-brand-sage/30", icon: Check, order: 3 },
  atrasada: { label: "Atrasada", c: "bg-destructive/10 text-destructive border-destructive/20", icon: AlertTriangle, order: 0 },
  pendiente: { label: "Pendiente", c: "bg-amber-50 text-amber-600 border-amber-200", icon: Clock, order: 1 },
  proxima: { label: "Próxima", c: "bg-blue-50 text-blue-600 border-blue-200", icon: Calendar, order: 2 },
  futura: { label: "Futura", c: "bg-stone-50 text-muted-foreground border-stone-200", icon: Calendar, order: 4 },
};

const ageLabel = (m) => (m === 0 ? "Nacimiento" : m < 24 ? `${m} meses` : `${Math.floor(m / 12)} años`);

export default function VaccinationCard({ patient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/patients/${patient.patient_id}/vaccination`); setData(res.data); }
    catch (e) { toast.error("No se pudo cargar la cartilla"); }
    finally { setLoading(false); }
  }, [patient.patient_id]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    const applied = !item.applied;
    setData((d) => ({ ...d, items: d.items.map((i) => i.key === item.key ? { ...i, applied, status: applied ? "aplicada" : "pendiente", applied_date: applied ? new Date().toISOString() : null } : i) }));
    try {
      await api.post(`/patients/${patient.patient_id}/vaccination`, { key: item.key, applied, date: new Date().toISOString() });
      load();
    } catch (e) { toast.error("Error al actualizar"); load(); }
  };

  const markAllForAge = async () => {
    if (!data) return;
    // Vacunas que ya corresponden por edad y aún no están aplicadas (no incluye las futuras).
    const pending = data.items.filter((i) => !i.applied && i.age_months <= data.age_months);
    const keys = pending.map((i) => i.key);
    if (keys.length === 0) { toast.info("No hay vacunas pendientes para su edad."); return; }
    setMarkingAll(true);
    try {
      await api.post(`/patients/${patient.patient_id}/vaccination/bulk`, { keys, applied: true, date: new Date().toISOString() });
      toast.success(`${keys.length} vacuna(s) marcadas como aplicadas. Puedes desmarcar las que no apliquen.`);
      load();
    } catch (e) { toast.error("No se pudo marcar todas"); }
    finally { setMarkingAll(false); }
  };

  if (loading) return <div className="py-16 flex justify-center"><div className="w-8 h-8 rounded-full border-[3px] border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>;
  if (!data) return null;

  const applied = data.items.filter((i) => i.applied).length;
  const overdue = data.items.filter((i) => i.status === "atrasada").length;
  const pendingForAge = data.items.filter((i) => !i.applied && i.age_months <= data.age_months).length;
  const sorted = [...data.items].sort((a, b) => (STATUS[a.status].order - STATUS[b.status].order) || (a.age_months - b.age_months));

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl p-6 border border-stone-100 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-terracotta/10 flex items-center justify-center text-brand-terracotta"><Syringe size={24} /></div>
          <div>
            <h3 className="font-outfit font-bold text-brand-ink">Cartilla de vacunación</h3>
            <p className="text-sm text-muted-foreground">{applied} aplicadas · {overdue} atrasadas · edad {ageLabel(data.age_months)}</p>
          </div>
        </div>
        <button data-testid="mark-all-age-btn" onClick={markAllForAge} disabled={markingAll || pendingForAge === 0}
          className="flex items-center gap-2 bg-brand-sage hover:bg-brand-sageDark text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100">
          {markingAll ? <Loader2 size={16} className="animate-spin" /> : <CheckCheck size={16} />}
          Marcar todas las de su edad{pendingForAge > 0 ? ` (${pendingForAge})` : ""}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3" data-testid="vaccination-list">
        {sorted.map((item, i) => {
          const st = STATUS[item.status]; const Icon = st.icon;
          return (
            <motion.button key={item.key} data-testid={`vax-${item.key}`} onClick={() => toggle(item)}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              className={`text-left bg-white rounded-2xl p-4 border ${item.applied ? "border-brand-sage/30" : "border-stone-100"} hover:shadow-md transition-all flex items-start gap-3`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${st.c}`}><Icon size={16} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-outfit font-semibold text-sm text-brand-ink">{item.vaccine}</p>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${item.group === "gratuita" ? "bg-brand-sage/15 text-brand-sageDark" : "bg-brand-terracotta/10 text-brand-terracotta"}`}>{item.group === "gratuita" ? "Gratuita" : "Adicional"}</span>
                </div>
                <p className="text-xs text-muted-foreground">{item.dose} · {ageLabel(item.age_months)} · {item.protege}</p>
                <p className={`text-xs font-bold mt-1 ${st.c.split(" ").find((x) => x.startsWith("text-"))}`}>
                  {st.label}{item.applied_date ? ` · ${new Date(item.applied_date).toLocaleDateString("es-MX")}` : ""}
                </p>
              </div>
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${item.applied ? "bg-brand-sage border-brand-sage" : "border-stone-300"}`}>
                {item.applied && <Check size={13} className="text-white" />}
              </div>
            </motion.button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">Toca una vacuna para marcarla como aplicada (con fecha de hoy). Esquema de referencia (México).</p>
    </div>
  );
}
